/**
 * Displays dropdown and input for government ID when travel rule is triggered on
 * the selected funding option and the user must enter a form of goverment ID.
 * The component displays a dropdown with options for each type of government ID.
 * The input displayed below the dropdown allows the user to enter an ID, and
 * displays the correct labels and placeholders depending on which type of government
 * ID is selected.
 */

import PropTypes from 'prop-types'

import React from 'react'
import classnames from 'classnames'
import { StyleSheet, css } from 'lib/paypalphrodite'
import {
  TextInput as PPTextInput,
  DropdownMenu,
  Row,
  Col,
} from '@paypalcorp/pp-react'
import TextInput from 'react-text-input'
import * as ViewActions from '../actions/Actions'
import { GOVT_ID_TYPES } from '../constants'
import { isGovernmentIdValid } from '../utils/travelRuleUtils'
import { isDw2Repaint } from '../../experiments/dw2Repaint'
// eslint-disable-next-line i18n-scan/no-third-party-l10n-libraries
import content from 'pp-react-l10n'
import { hasGuestFlowRevamp } from '../../experiments/guest'
let i18n = content('transfer/send/identityInfo')

const STYLES = StyleSheet.create({
  inputs: {
    width: '49%',
    marginRight: '2%',
    display: 'inline-block',
    verticalAlign: 'bottom',
    ':last-child': {
      marginRight: 0,
    },
  },
  governmentIdClass: {
    '@media (max-width: 768px)': {
      marginTop: '1em',
    },
    '@media (min-width: 769px)': {
      paddingLeft: 0,
    },
  },
})

const governmentIdTypesInfo = {
  [GOVT_ID_TYPES.SSN]: {
    contentLabel: 'identityInfo.ssn',
    placeholder: 'xxx-xx-xxxx',
    pattern: '(^\\d{3}-?\\d{2}-?\\d{4}$|^XXX-XX-XXXX$)',
    maxLength: null,
  },
  [GOVT_ID_TYPES.ITIN]: {
    contentLabel: 'identityInfo.tin',
    placeholder: 'xx-xxxxxxx',
    pattern: '(^\\d{2}-?\\d{7}$|^XX-XXXXXXX$)',
    maxLength: null,
  },
  [GOVT_ID_TYPES.ALIEN]: {
    contentLabel: 'identityInfo.alienId',
    placeholder: '',
    pattern: '(^.*$)',
    maxLength: 9,
  },
  [GOVT_ID_TYPES.PASSPORT]: {
    contentLabel: 'identityInfo.passport',
    placeholder: '',
    pattern: '(^.*$)',
    maxLength: 9,
  },
}
class TravelRuleGovernmentId extends React.Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    selectedGovernmentIdType: PropTypes.string.isRequired,
    governmentId: PropTypes.string.isRequired,
    attemptedSubmit: PropTypes.bool.isRequired,
  }

  state = {
    hasBlurredOnce: false,
    isFocused: false,
  }

  handleChangeGovernmentId = event => {
    this.props.dispatch(ViewActions.changeGovernmentId(event.target.value))
  }

  handleChangeSelectedGovernmentIdType = event => {
    this.props.dispatch(
      ViewActions.changeSelectedGovernmentIDType(event.target.value)
    )
    this.refs.input.clearAndFocusInput()
  }

  renderDropdownOptions = () => {
    const governmentIdTypes = Object.keys(GOVT_ID_TYPES)
    return governmentIdTypes.map(idType => {
      const key = GOVT_ID_TYPES[idType]
      const { contentLabel } = governmentIdTypesInfo[key]
      if (isDw2Repaint() || hasGuestFlowRevamp()) {
        return {
          value: key,
          primaryText: i18n(contentLabel),
        }
      }
      return (
        <option key={key} value={key}>
          {i18n(contentLabel)}
        </option>
      )
    })
  }

  handleFocus = () => {
    this.setState({ isFocused: true })
  }

  handleBlur = () => {
    this.setState({
      hasBlurredOnce: true,
      isFocused: false,
    })
  }

  render() {
    const {
      governmentId,
      selectedGovernmentIdType,
      attemptedSubmit,
    } = this.props
    const { isFocused, hasBlurredOnce } = this.state
    const { placeholder, maxLength, contentLabel } = governmentIdTypesInfo[
      selectedGovernmentIdType
    ]
    const shouldShowError =
      !isGovernmentIdValid(governmentId, selectedGovernmentIdType) &&
      !isFocused &&
      (hasBlurredOnce || attemptedSubmit)

    if (isDw2Repaint() || hasGuestFlowRevamp()) {
      return (
        <Row>
          <Col xs={12} md={8}>
            <DropdownMenu
              required={true}
              id="identifiers"
              label={i18n('identityInfo.identityInfoId')}
              options={this.renderDropdownOptions()}
              data-nemo="identifiers"
              value={selectedGovernmentIdType}
              onChange={this.handleChangeSelectedGovernmentIdType}
            />
          </Col>
          <Col
            xs={12}
            md={4}
            className={classnames(css(STYLES.governmentIdClass))}
          >
            <PPTextInput
              value={governmentId}
              type="text"
              name="identifierValue"
              id="identifierValue"
              data-nemo="identifier-value"
              ref="input"
              label={placeholder || i18n(contentLabel)}
              errorText={
                shouldShowError && i18n('identityInfo.identifierValue')
              }
              maxLength={maxLength}
              theme="v2"
              onChange={this.handleChangeGovernmentId}
              onFocus={this.handleFocus}
              onBlur={this.handleBlur}
            />
          </Col>
        </Row>
      )
    }
    return (
      <div>
        <div>
          <label htmlFor="identifiers">
            {i18n('identityInfo.identifiers')}
          </label>
          <div>
            {' '}
            {/* div needed to drop the dropdowns down to a new line (since they're not 100% width) */}
            {/* disabling linting requirement, but should be up to the dev what is needed */
            /* eslint-disable-next-line jsx-a11y/no-onchange */}
            <select
              id="identifiers"
              data-nemo="identifiers"
              className={classnames('vx_form-control', css(STYLES.inputs))}
              required={true}
              value={selectedGovernmentIdType}
              onChange={this.handleChangeSelectedGovernmentIdType}
            >
              {this.renderDropdownOptions()}
            </select>
            <div
              className={classnames(css(STYLES.inputs), {
                'vx_has-error': shouldShowError,
              })}
              style={{ display: 'inline-block' }}
            >
              <TextInput
                value={governmentId}
                type="text"
                name="identifierValue"
                id="identifierValue"
                data-nemo="identifier-value"
                className={classnames(
                  'vx_form-control vx_form-control_complex'
                )}
                ref="input"
                label={placeholder || i18n(contentLabel)}
                maxLength={maxLength}
                onChange={this.handleChangeGovernmentId}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }
}

export default TravelRuleGovernmentId
